<template>
  <v-container fluid class="filter-container">
    <v-expansion-panel>
      <v-expansion-panel-content>
        <template v-slot:header>
          <div class="filter-header" role="button" aria-expanded="false">
            <v-badge overlap left>
              <template v-slot:badge>
                <span v-if="getActiveFiltersCount() > 0" aria-label="Active filters count">{{
                  getActiveFiltersCount()
                }}</span>
              </template>
              <v-icon large class="mr-3" aria-hidden="true">mdi-filter-variant</v-icon>
            </v-badge>
            <span id="filter-heading">{{ $i18n.t("Ticket Filters") }}</span>
          </div>
        </template>

        <v-card class="filter-card elevation-3" aria-labelledby="filter-heading">
          <v-card-text>
            <ActiveFilters :active-filters="activeFiltersDisplay" @remove-filter="removeFilter" />

            <v-layout row wrap>
              <FilterField
                v-for="(field, key) in textFilterFields"
                :key="key"
                v-model="filters[key]"
                v-bind="field"
                @toggle-condition="toggleCondition(key)"
              />
              <FilterField
                v-for="(field, key) in filterFields"
                :key="key"
                v-model="filters[key]"
                v-bind="field"
                @toggle-condition="toggleCondition(key)"
              />
            </v-layout>

            <v-layout row wrap justify-center class="date-filters-container">
              <DateRangeFilter
                v-for="(field, key) in dateRangeFilterFields"
                :key="key"
                v-model="filters[key]"
                :from-date-menu.sync="field.fromDateMenu"
                :to-date-menu.sync="field.toDateMenu"
                :title="field.placeholder"
              />
            </v-layout>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="filter-actions p-0">
            <v-spacer></v-spacer>
            <v-btn small flat class="reset-button" @click="resetFilters" aria-label="$i18n.t('Reset all filters') }">
              <v-icon left aria-hidden="true">mdi-refresh</v-icon>
              {{ $i18n.t("Reset") }}
            </v-btn>
            <v-btn
              small
              color="primary"
              class="apply-button"
              @click="applyFilters"
              :disabled="!hasActiveFilters"
              :aria-label="$i18n.t(' Apply Filters')"
            >
              <v-icon left aria-hidden="true">mdi-filter</v-icon>
              {{ $i18n.t("Apply Filters") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import ActiveFilters from "@/components/filter/ActiveFilters.vue";
import FilterField from "@/components/filter/FilterField.vue";
import DateRangeFilter from "@/components/filter/DateRangeFilter.vue";
import { SEVERITY_TYPES, REQUEST_TYPES, TICKET_STATUS, USER_TYPE } from "@/constants.js";

export default {
  name: "TicketFilterForm",
  components: {
    ActiveFilters,
    FilterField,
    DateRangeFilter
  },
  data() {
    return {
      textFilterFields: {
        _id: { type: "number", placeholder: this.$i18n.t("Ticket N°"), icon: "mdi-pound" },
        title: { type: "text", placeholder: this.$i18n.t("Title"), icon: "mdi-format-title" }
      },
      dateRangeFilterFields: {
        createdDateRange: {
          type: "date",
          placeholder: this.$i18n.t("Created on"),
          fromDateMenu: false,
          toDateMenu: false
        },
        updatedDateRange: {
          type: "date",
          placeholder: this.$i18n.t("Updated on"),
          fromDateMenu: false,
          toDateMenu: false
        }
      },
      isContractFieldOpen: false,

      debugMapping: {}
    };
  },
  computed: {
    ...mapGetters({
      softwareList: "software/getSoftwareList",
      userList: "users/getUsers",
      clientsList: "client/getClients",
      contractList: "contract/getContractsMeta",
      statusList: "filter/getStatusOptions",
      advancedFilters: "filter/getAdvancedFilters",
      getUser: "currentUser/getUser",
      UserContracts: "currentUser/getContracts"
    }),
    isUserExpert() {
      return this.getUser && this.getUser.type === USER_TYPE.EXPERT;
    },
    clientIds() {
      return this.clientList.map(cl => cl._id);
    },
    contractUsers() {
      if (this.isUserExpert) {
        return this.userList;
      } else {
        return this.userList.filter(cl => this.clientIds.includes(cl.client) || cl.client === null);
      }
    },
    contractsList() {
      if (this.isUserExpert) {
        return this.contractList;
      } else {
        return this.contractList.filter(cl => this.clientIds.includes(cl.clientId));
      }
    },
    clientList() {
      let list = this.clientsList;
      if (this.isUserExpert) {
        return list;
      } else {
        return this.clientsList.filter(cl => cl._id === this.getUser.client);
      }
    },
    filters: {
      get() {
        return this.advancedFilters || {};
      },
      set(value) {
        this.$store.dispatch("filter/setAdvancedFilters", value);
      }
    },
    filterFields() {
      return {
        type: {
          type: "select",
          placeholder: this.$i18n.t("Type"),
          icon: "mdi-shape-outline",
          items: REQUEST_TYPES,
          multiple: true
        },
        severity: {
          type: "select",
          placeholder: this.$i18n.t("Severity"),
          icon: "mdi-alert-outline",
          items: SEVERITY_TYPES,
          multiple: true
        },
        softwareName: {
          type: "autocomplete",
          placeholder: this.$i18n.t("Software"),
          icon: "mdi-laptop",
          items: this.softwareList.map(software => ({ text: software.name, value: software._id })),
          multiple: true
        },
        assignedToName: {
          type: "autocomplete",
          placeholder: this.$i18n.t("Assigned to"),
          icon: "mdi-account-check-outline",
          items: this.contractUsers.map(user => ({ text: user.name, value: user.user, type: user.type })),
          multiple: true
        },
        responsibleName: {
          type: "autocomplete",
          placeholder: this.$i18n.t("Responsible"),
          icon: "mdi-account-outline",
          items: this.contractUsers.map(user => ({ text: user.name, value: user.user, type: user.type })),
          multiple: true
        },
        beneficiaryName: {
          type: "autocomplete",
          placeholder: this.$i18n.t("Beneficiary"),
          icon: "mdi-account-multiple-outline",
          items: this.contractUsers
            .filter(user => user.type === "beneficiary")
            .map(user => ({ text: user.name, value: user.user, type: user.type })),
          multiple: true
        },
        clientName: {
          type: "autocomplete",
          placeholder: this.$i18n.t("Client"),
          icon: "mdi-account-outline",
          items: this.clientList.map(client => ({ text: client.name, value: client._id })),
          multiple: true
        },
        contract: {
          type: "autocomplete",
          placeholder: this.$i18n.t("Contract"),
          icon: "mdi-briefcase-account-outline",
          items: this.contractsList.map(contract => ({ text: contract.name, value: contract._id })),
          multiple: true,
          onOpen: this.handleContractFieldOpen
        },
        status: {
          type: "select",
          placeholder: this.$i18n.t("Status"),
          icon: "mdi-flag-outline",
          items: Object.values(TICKET_STATUS),
          multiple: true
        }
      };
    },

    placeholderToKeyMap() {
      const map = {};

      Object.keys(this.textFilterFields).forEach(key => {
        const field = this.textFilterFields[key];
        if (field.placeholder) {
          map[field.placeholder] = key;
        }
      });

      Object.keys(this.filterFields).forEach(key => {
        const field = this.filterFields[key];
        if (field.placeholder) {
          map[field.placeholder] = key;
        }
      });

      Object.keys(this.dateRangeFilterFields).forEach(key => {
        const field = this.dateRangeFilterFields[key];
        if (field.placeholder) {
          map[field.placeholder] = key;
        }
      });

      return map;
    },
    fieldsWithTextValue() {
      return Object.keys(this.filterFields).filter(key => {
        const field = this.filterFields[key];
        return (
          field.items &&
          field.items.length > 0 &&
          typeof field.items[0] === "object" &&
          "text" in field.items[0] &&
          "value" in field.items[0]
        );
      });
    },
    hasActiveFilters() {
      return Object.values(this.filters).some(value => {
        if (typeof value === "object" && value !== null) {
          return (value.values && value.values.length > 0) || (value.from || value.to);
        }
        return value !== null && value !== "";
      });
    },
    activeFiltersDisplay() {
      const display = {};

      Object.keys(this.filters).forEach(key => {
        let fieldConfig = null;
        if (key in this.textFilterFields) {
          fieldConfig = this.textFilterFields[key];
        } else if (key in this.filterFields) {
          fieldConfig = this.filterFields[key];
        } else if (key in this.dateRangeFilterFields) {
          fieldConfig = this.dateRangeFilterFields[key];
        }

        if (!fieldConfig) return;

        const filter = this.filters[key];
        if (
          filter &&
          ((Array.isArray(filter.values) && filter.values.length > 0) ||
            (typeof filter === "string" && filter !== "") ||
            ((key === "createdDateRange" || key === "updatedDateRange") && (filter.from || filter.to)))
        ) {
          let displayKey = fieldConfig.placeholder;
          if (key === "_id") displayKey = "Ticket Number";

          let displayValue = filter.values || filter;

          if (this.fieldsWithTextValue.includes(key)) {
            const options = this.filterFields[key].items;
            displayValue = filter.values.map(value => {
              const option = options.find(opt => opt.value === value);
              return option ? option.text : value;
            });
          }

          if (Array.isArray(displayValue)) {
            const condition = filter.condition === "in" ? "=" : "≠";
            displayValue = `${condition} ${displayValue.join(", ")}`;
          }

          if (key === "createdDateRange" || key === "updatedDateRange") {
            displayValue = `${filter.from || ""} to ${filter.to || ""}`;
          }

          display[displayKey] = displayValue;
        }
      });
      return display;
    }
  },
  created() {
    this.$store.dispatch("filter/fetchAdvancedFilters");
    this.$store.dispatch("software/fetchSoftwareList");
    this.$store.dispatch("users/fetchUsers");
    this.$store.dispatch("client/fetchClients");
    this.$store.dispatch("currentUser/fetchUser");

    this.$watch(
      () => this.filters.type.values,
      (newValues, oldValues) => {
        if (newValues.includes("Other") && !oldValues.includes("Other")) {
          console.log("Adding 'Other' to type filter");
          this.filters["type"].values = ["Other"];
        } else if (newValues.length > 1 && newValues.includes("Other") && oldValues.includes("Other")) {
          this.filters["type"].values = [...newValues.filter(value => value !== "Other")];
        }
      },
      { deep: true }
    );
  },
  methods: {
    resetFilters() {
      this.$store.dispatch("filter/resetAdvancedFilters");
      this.$store.dispatch("ticket/fetchTickets");
    },
    applyFilters() {
      const activeFilters = {};

      Object.keys(this.filters).forEach(key => {
        const filter = this.filters[key];
        if (
          filter &&
          ((Array.isArray(filter.values) && filter.values.length > 0) ||
            (typeof filter === "string" && filter !== "") ||
            ((key === "createdDateRange" || key === "updatedDateRange") && (filter.from || filter.to)))
        ) {
          if (typeof filter === "object" && key !== "createdDateRange" && key !== "updatedDateRange") {
            activeFilters[key] = {
              condition: filter.condition,
              values: filter.values
            };
          } else {
            activeFilters[key] = filter;
          }
        }
      });

      this.$store.dispatch("filter/setAdvancedFilters", this.filters);
      this.$store.dispatch("ticket/resetTickets");
      this.$store.dispatch("ticket/fetchTickets");
    },
    removeFilter(displayKey) {
      console.log("Removing filter:", displayKey);
      console.log("Mapping:", this.debugMapping);

      const actualKey = this.placeholderToKeyMap[displayKey];
      console.log("Actual key:", actualKey);

      if (actualKey) {
        const updatedFilters = { ...this.filters };
        if (actualKey === "createdDateRange" || actualKey === "updatedDateRange") {
          updatedFilters[actualKey] = { from: null, to: null };
        } else if (typeof updatedFilters[actualKey] === "object" && updatedFilters[actualKey] !== null) {
          updatedFilters[actualKey].values = [];
        } else {
          updatedFilters[actualKey] = "";
        }
        this.$store.dispatch("filter/setAdvancedFilters", updatedFilters);
      } else {
        this.removeFilterByDisplayKey(displayKey);
      }
    },

    removeFilterByDisplayKey(displayKey) {
      const normalizedKey = displayKey.toLowerCase().replace(/\s+/g, "");

      const actualKey = Object.keys(this.filters).find(key => {
        if (key.toLowerCase() === normalizedKey) return true;

        const fieldConfig = this.textFilterFields[key] || this.filterFields[key] || this.dateRangeFilterFields[key];

        if (fieldConfig && fieldConfig.placeholder) {
          return fieldConfig.placeholder.toLowerCase().replace(/\s+/g, "") === normalizedKey;
        }

        return false;
      });

      if (actualKey) {
        const updatedFilters = { ...this.filters };
        if (actualKey === "createdDateRange" || actualKey === "updatedDateRange") {
          updatedFilters[actualKey] = { from: null, to: null };
        } else if (typeof updatedFilters[actualKey] === "object" && updatedFilters[actualKey] !== null) {
          updatedFilters[actualKey].values = [];
        } else {
          updatedFilters[actualKey] = "";
        }
        this.$store.dispatch("filter/setAdvancedFilters", updatedFilters);
      }
    },
    toggleCondition(filterKey) {
      if (this.filters[filterKey] && this.filters[filterKey].condition) {
        const updatedFilters = { ...this.filters };
        updatedFilters[filterKey].condition = updatedFilters[filterKey].condition === "in" ? "not in" : "in";
        this.$store.dispatch("filter/setAdvancedFilters", updatedFilters);
      }
    },
    handleContractFieldOpen() {
      if (!this.isContractFieldOpen && !this.contractsList.length) {
        this.isContractFieldOpen = true;
        this.$store.dispatch("contract/fetchContractsMeta");
      }
    },
    getActiveFiltersCount() {
      return Object.values(this.filters).filter(value => {
        if (typeof value === "string") {
          return value.length > 0;
        }
        if (value && typeof value === "object") {
          if (Array.isArray(value.values)) {
            return value.values.length > 0;
          }
          if (value.from != null || value.to != null) {
            return true;
          }
        }
        return false;
      }).length;
    }
  }
};
</script>

<style scoped>
.mdi::before,
.mdi-set {
  font-size: 24px;
}

.filter-container {
  padding: 16px;
  padding-left: 0;
  padding-right: 0;
}

.filter-header {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
}

.filter-card {
  border-radius: 4px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.date-filters-container {
  margin-top: 8px;
  justify-content: center;
}

.v-card__actions {
  padding: 6px !important;
}

.v-card__text {
  padding: 6px !important;
}

.v-expansion-panel__header {
  padding: 6px !important;
}

.reset-button {
  margin-right: 8px;
}

.apply-button {
  margin-left: 8px;
}

.v-btn:focus,
.v-expansion-panel__header:focus {
  outline: 2px solid #2196f3;
  outline-offset: 2px;
}

.v-btn:focus:not(:hover),
.v-expansion-panel__header:focus:not(:hover) {
  outline: 2px solid #2196f3;
  outline-offset: 2px;
}

@media (max-width: 600px) {
  .filter-actions {
    flex-direction: column-reverse;
  }

  .reset-button,
  .apply-button {
    width: 100%;
    margin: 4px 0;
  }
}
</style>
